import { Autocomplete, Stack, TextField, Typography } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import React, { useState, useEffect, useRef } from 'react';
import Api from '../../Services/Api';
import * as Sentry from "@sentry/browser";
import { useTranslation } from 'react-i18next';

const ContactAutocomplete = ({opts, contact, setContact}) => {
    // Pre-seed options with the current contact so defaultValue resolves correctly
    const [options, setOptions] = useState(() =>
        opts?.length > 0 ? opts : (contact?.id && contact?.firstname ? [contact] : [])
    );
    const [after, setAfter] = useState(undefined);
    const previousController = useRef();
    const { t } = useTranslation();

    useEffect(() => {
        if (opts != undefined && opts.length > 0) {
            setOptions(opts);
        } else if (contact?.id && contact?.firstname && options.length === 0) {
            setOptions([contact]);
        }
    }, [opts]);

    const onInputChangeContact = (event, value, reason) => {
        if (value && reason === "input") {
            if (previousController.current) {
                previousController.current.abort();
            }
            const controller = new AbortController();
            const signal = controller.signal;
            previousController.current = controller;

            Api.fetchContacts({firstName: value, after: after, signal: signal}).then((result) => {
                if(result.options.length > 0) {
                    setOptions(result.options);
                }
                setAfter(result.after);
            }).catch((error) => {
                setAfter(undefined);
                console.log(error);
            });
        }
    };

    return (
        <Autocomplete
            disablePortal
            id="contact-autocomplete"
            style={{ width: "100%" }}
            defaultValue={options.filter((option) => option.id === contact.id)[0]}
            onInputChange={onInputChangeContact}
            onChange={(event, newValue) => {
                if (newValue != null && newValue.id != null) {
                    setContact({ id: newValue.id, firstname: newValue.firstname, lastname: newValue.lastname, email: newValue.email, create: false, options: options });
                }
            }}
            options={options.sort((a, b) => {
                if (a != null && a.firstname != null && b.firstname != null) return a.firstname.localeCompare(b.firstname);
                return 0;
            })}
            groupBy={(option) => {
                if (option && option.firstname && option.firstname.length > 0) {
                    return option.firstname.charAt(0).toUpperCase();
                }
                return '';
            }}
            getOptionLabel={(option) => {
                if (option != null) {
                    return `${option.firstname} ${option.lastname} ${option.email ? " - " + option.email : ""}`;
                }
                return "";
            }}
            slotProps={{
                paper: {
                    sx: {
                        '& .MuiAutocomplete-option': {
                            minHeight: 52,
                            padding: '10px 16px',
                        }
                    }
                }
            }}
            renderInput={(params) => <TextField {...params} label={t('selectContact')} />}
            renderOption={(props, option) => (
                <Stack component="li" direction="row" alignItems="center" spacing={1.5}
                    sx={{ minHeight: 56, py: 1, px: 2 }} {...props}>
                    <Avatar sx={{
                        width: 36, height: 36,
                        backgroundColor: 'rgba(118,169,172,0.3)',
                        color: '#76A9AC', fontSize: '0.875rem', fontWeight: 600,
                    }}>
                        {(option.firstname?.[0] || '').toUpperCase()}{(option.lastname?.[0] || '').toUpperCase()}
                    </Avatar>
                    <Stack spacing={0}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {option.firstname} {option.lastname}
                        </Typography>
                        {option.email && (
                            <Typography variant="caption" color="text.secondary">
                                {option.email}
                            </Typography>
                        )}
                    </Stack>
                </Stack>
            )}
        />
    );
}

export default ContactAutocomplete;
